import { Sort } from '@angular/material/sort'
import { Location } from '@angular/common'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store'
import { isEmpty, isObject, omit, pick, pickBy } from 'lodash'
import {
  SetFilters,
  SetFiltersFromUrlQueryParams,
  SetAdvancedTableFilters,
  SetUrlQueryParamsFromState,
  RestoreFiltersFromLocalStorage
} from './filters.actions'
import { FiltersStateModel, FilterStateKeys } from './filters.model'
import { AppVersion, PageQueryParamsMap, PersistFilters, NewVersionIndicator } from './filters.constant'
import { jwtDecode } from 'jwt-decode'
import sign from 'jwt-encode'
import { FullReceivableType, InvoicePackageGroup } from '@lla-platform/receivable/receivable-data-access'
import { PeriodType, PeriodTypeButtonsInfo } from '../../enums/period-type.enum'
import { LOCAL_STORAGE_KEYS, environment } from '@lla-platform/core/core-util'
import { ITableFilter } from '../../interfaces/advanced-table.interface'
import { TargetType } from '@lla-platform/targets/targets-data-access'
import { IRangeDateInfo, ITimePeriodWithLocationIds } from '../../interfaces/time-period.interface'

/** Default for a per-location period filter no page has set yet: the period's date range, all locations. */
const defaultPeriodFilter = (periodType: PeriodType): ITimePeriodWithLocationIds => ({
  periodType,
  locationIds: [],
  ...PeriodTypeButtonsInfo().find((el) => el.value === periodType)?.extraInfo
})

@State<FiltersStateModel>({
  name: 'filters'
})
@Injectable()
export class FiltersState implements NgxsOnInit {
  constructor(
    private location: Location,
    private router: Router
  ) {}

  @Selector()
  static colorMode(state: FiltersStateModel) {
    return state.colorMode
  }

  @Selector()
  static isLoading(state: FiltersStateModel) {
    return state.isLoading
  }

  @Selector()
  static dashboardGroup(state: FiltersStateModel) {
    return state.dashboardGroup
  }

  @Selector()
  static rangePickerType(state: FiltersStateModel) {
    return state.rangePickerType
  }

  @Selector()
  static tireWidgetFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.tireWidgetFilter ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }
    )
  }

  @Selector()
  static contactsTypeWidgetFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.contactsTypeWidgetFilter ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }
    )
  }

  @Selector()
  static arReceivablesWidgetFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.arReceivablesWidgetFilter ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }
    )
  }

  @Selector()
  static receivableListFilters(state: FiltersStateModel) {
    return state.receivableListFilters ?? { page: 1 }
  }

  @Selector()
  static receivableColumnsVisibility(state: FiltersStateModel) {
    return state.receivableColumnsVisibility
  }

  @Selector()
  static receivableListAdvancedMode(state: FiltersStateModel) {
    return state.receivableListAdvancedMode
  }

  @Selector()
  static shopReceivableSummaryFilter(state: FiltersStateModel) {
    return (
      state.shopReceivableSummaryFilter ?? {
        locationIds: [],
        receivablesType: FullReceivableType.All
      }
    )
  }

  @Selector()
  static receivableSnapshotFilter(state: FiltersStateModel) {
    return state.receivableSnapshotFilter
  }

  @Selector()
  static accountReceivableFilter(state: FiltersStateModel) {
    return (
      state.accountReceivableFilter ?? {
        locationIds: [],
        receivablesType: FullReceivableType.All
      }
    )
  }

  @Selector()
  static arSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.arSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static callsReportFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.YearToDate)?.extraInfo
    return (
      state.callsReportFilter ?? {
        periodType: PeriodType.YearToDate,
        ...dateInfo
      }
    )
  }

  @Selector()
  static invoicesListFilters(state: FiltersStateModel) {
    return state.invoicesListFilters ?? { page: 1 }
  }

  @Selector()
  static invoicesListGetData(state: FiltersStateModel) {
    return state.invoicesListGetData
  }

  @Selector()
  static invoicesListColumnsVisibility(state: FiltersStateModel) {
    return state.invoicesListColumnsVisibility
  }

  @Selector()
  static invoicesListAdvancedMode(state: FiltersStateModel) {
    return state.invoicesListAdvancedMode
  }

  @Selector()
  static invoicesListReturnUrl(state: FiltersStateModel) {
    return state.invoicesListReturnUrl
  }

  @Selector()
  static grossListFilters(state: FiltersStateModel) {
    return state.grossListFilters ?? { page: 1 }
  }

  @Selector()
  static grossListColumnsVisibility(state: FiltersStateModel) {
    return state.grossListColumnsVisibility
  }

  @Selector()
  static grossListAdvancedMode(state: FiltersStateModel) {
    return state.grossListAdvancedMode
  }

  @Selector()
  static grossListReturnUrl(state: FiltersStateModel) {
    return state.grossListReturnUrl
  }

  @Selector()
  static invoiceLinesFilters(state: FiltersStateModel) {
    return state.invoiceLinesFilters ?? { page: 1 }
  }

  @Selector()
  static invoiceLinesGetData(state: FiltersStateModel) {
    return state.invoiceLinesGetData
  }

  // Looked up by name: the filter widgets select FiltersState[advancedFilterKey]
  @Selector()
  static invoiceLinesAdvancedMode(state: FiltersStateModel) {
    return state.invoiceLinesAdvancedMode
  }

  @Selector()
  static gpPackageListFilters(state: FiltersStateModel) {
    return state.gpPackageListFilters ?? { page: 1 }
  }

  @Selector()
  static gpPackageListColumnsVisibility(state: FiltersStateModel) {
    return state.gpPackageListColumnsVisibility
  }

  @Selector()
  static gpPackageListAdvancedMode(state: FiltersStateModel) {
    return state.gpPackageListAdvancedMode
  }

  @Selector()
  static gpPackageListReturnUrl(state: FiltersStateModel) {
    return state.gpPackageListReturnUrl
  }

  @Selector()
  static receivableListReturnUrl(state: FiltersStateModel) {
    return state.receivableListReturnUrl
  }

  @Selector()
  static showLargeMenu(state: FiltersStateModel) {
    return this.evaluateBoolean(state.showLargeMenu)
  }

  @Selector()
  static collapsedMenuSections(state: FiltersStateModel) {
    return state.collapsedMenuSections ?? []
  }

  @Selector()
  static showAppliedTableFilters(state: FiltersStateModel) {
    return this.evaluateBoolean(state.showAppliedTableFilters)
  }

  @Selector()
  static urlFilterParam() {
    const currentUrl = new URL(document.URL)
    return Object.fromEntries(new URLSearchParams(currentUrl.search) as any)['filters']
  }

  @Selector()
  static customersSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.customersSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static deferredTypesSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.deferredTypesSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static deferredTypesSortState(state: FiltersStateModel) {
    return state.deferredTypesSortState
  }

  @Selector()
  static grossProfitSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.grossProfitSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static grossProfitSortState(state: FiltersStateModel) {
    return state.grossProfitSortState
  }

  @Selector()
  static gpPackageByLocationSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return {
      ...(state.gpPackageByLocationSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }),
      invoicePackageGroupBy: InvoicePackageGroup.Location
    }
  }

  @Selector()
  static gpPackageByLocationSortState(state: FiltersStateModel) {
    return state.gpPackageByLocationSortState
  }

  @Selector()
  static gpPackageByCategorySummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return {
      ...(state.gpPackageByCategorySummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }),
      invoicePackageGroupBy: InvoicePackageGroup.Category
    }
  }

  @Selector()
  static gpPackageByCategorySortState(state: FiltersStateModel) {
    return state.gpPackageByCategorySortState
  }

  @Selector()
  static categoryOverviewFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.categoryOverviewFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static categoryOverviewTableFilters(state: FiltersStateModel) {
    return state.categoryOverviewTableFilters ?? { page: 1 }
  }

  @Selector()
  static customersListColumnsVisibility(state: FiltersStateModel) {
    return state.customersListColumnsVisibility
  }

  @Selector()
  static customersListFilters(state: FiltersStateModel) {
    return state.customersListFilters
  }

  @Selector()
  static listPagesPath(state: FiltersStateModel) {
    return state.listPagesPath
  }

  @Selector()
  static clientDetailReturnUrl(state: FiltersStateModel) {
    return state.clientDetailReturnUrl
  }

  @Selector()
  static communicationsListColumnsVisibility(state: FiltersStateModel) {
    return state.communicationsListColumnsVisibility
  }

  @Selector()
  static communicationsListTableFilters(state: FiltersStateModel) {
    return state.communicationsListTableFilters
  }

  @Selector()
  static customersSummaryViewMode(state: FiltersStateModel) {
    return state.customersSummaryViewMode ?? 'table'
  }

  @Selector()
  static weeklyTargetExpandedShopsId(state: FiltersStateModel) {
    return state.weeklyTargetExpandedShopsId ?? []
  }

  @Selector()
  static weeklyTargetScrollLocationId(state: FiltersStateModel) {
    return state.weeklyTargetScrollLocationId
  }

  @Selector()
  static weeklyTargetSelectedWeekNumber(state: FiltersStateModel) {
    return state.weeklyTargetSelectedWeekNumber
  }

  @Selector()
  static weeklyTargetSelectedDivision(state: FiltersStateModel) {
    return state.weeklyTargetSelectedDivision
  }

  @Selector()
  static TargetRankingByLocationSelectedState(state: FiltersStateModel) {
    return state.TargetRankingByLocationSelectedState
  }

  @Selector()
  static arPaymentsSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.arPaymentsSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static salesReportFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.SixMonthToDate
    )?.extraInfo
    return (
      state.salesReportFilter ?? {
        periodType: PeriodType.SixMonthToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static salesReportSelectedObjectives(state: FiltersStateModel) {
    return state.salesReportSelectedObjectives
  }

  @Selector()
  static packageUnperformedSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.Yesterday)?.extraInfo
    // Spread first: filters saved before paging existed lack page/sortState
    const pagingDefaults: { page: number; sortState: Sort } = {
      page: 1,
      sortState: { active: 'margin', direction: 'asc' }
    }
    return {
      ...pagingDefaults,
      ...(state.packageUnperformedSummaryFilter ?? {
        periodType: PeriodType.Yesterday,
        ...dateInfo
      })
    }
  }

  @Selector()
  static paymentsListFilters(state: FiltersStateModel) {
    return state.paymentsListFilters ?? { page: 1 }
  }

  @Selector()
  static adminAnnouncementsListFilters(state: FiltersStateModel) {
    return state.adminAnnouncementsListFilters ?? { page: 1 }
  }

  @Selector()
  static adminCallTagsListFilters(state: FiltersStateModel) {
    return state.adminCallTagsListFilters ?? { page: 1 }
  }

  @Selector()
  static adminCallTagsListAdvancedMode(state: FiltersStateModel) {
    return state.adminCallTagsListAdvancedMode
  }

  @Selector()
  static adminAnnouncementsListAdvancedMode(state: FiltersStateModel) {
    return state.adminAnnouncementsListAdvancedMode
  }

  @Selector()
  static paymentsListColumnsVisibility(state: FiltersStateModel) {
    return state.paymentsListColumnsVisibility
  }

  @Selector()
  static paymentsListAdvancedMode(state: FiltersStateModel) {
    return state.paymentsListAdvancedMode
  }

  @Selector()
  static callIntelligenceLeadsListFilters(state: FiltersStateModel) {
    return state.callIntelligenceLeadsListFilters ?? { page: 1 }
  }

  @Selector()
  static callIntelligenceLeadsListColumnsVisibility(state: FiltersStateModel) {
    return state.callIntelligenceLeadsListColumnsVisibility
  }

  @Selector()
  static callIntelligenceLeadsListAdvancedMode(state: FiltersStateModel) {
    return state.callIntelligenceLeadsListAdvancedMode
  }

  @Selector()
  static callIntelligenceActiveLeadsId(state: FiltersStateModel) {
    return state.callIntelligenceActiveLeadsId
  }

  @Selector()
  static callIntelligenceReturnUrl(state: FiltersStateModel) {
    return state.callIntelligenceReturnUrl
  }

  @Selector()
  static callIntelligenceCostLeadsListFilters(state: FiltersStateModel) {
    return state.callIntelligenceCostLeadsListFilters ?? { page: 1 }
  }

  @Selector()
  static callIntelligenceCostLeadsListColumnsVisibility(state: FiltersStateModel) {
    return state.callIntelligenceCostLeadsListColumnsVisibility
  }

  @Selector()
  static callIntelligenceCostLeadsListAdvancedMode(state: FiltersStateModel) {
    return state.callIntelligenceCostLeadsListAdvancedMode
  }

  @Selector()
  static callIntelligenceCostActiveLeadsId(state: FiltersStateModel) {
    return state.callIntelligenceCostActiveLeadsId
  }

  @Selector()
  static vendorsListFilters(state: FiltersStateModel) {
    return state.vendorsListFilters ?? { page: 1 }
  }

  @Selector()
  static vendorsListColumnsVisibility(state: FiltersStateModel) {
    return state.vendorsListColumnsVisibility
  }

  @Selector()
  static vendorsListAdvancedMode(state: FiltersStateModel) {
    return state.vendorsListAdvancedMode
  }

  @Selector()
  static preferredVendorsFilters(state: FiltersStateModel) {
    return state.preferredVendorsFilters ?? { page: 1 }
  }

  @Selector()
  static preferredVendorsAdvancedMode(state: FiltersStateModel) {
    return state.preferredVendorsAdvancedMode
  }

  @Selector()
  static adminShopTiresExpandedShopsId(state: FiltersStateModel) {
    return state.adminShopTiresExpandedShopsId ?? []
  }

  @Selector()
  static promotionsExpandedShopsId(state: FiltersStateModel) {
    return state.promotionsExpandedShopsId ?? []
  }

  @Selector()
  static promotionsUsersExpandedIds(state: FiltersStateModel) {
    return state.promotionsUsersExpandedIds ?? []
  }

  @Selector()
  static tiresSummaryFilter(state: FiltersStateModel) {
    return state.tiresSummaryFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresSummaryViewMode(state: FiltersStateModel) {
    return state.tiresSummaryViewMode ?? 'table'
  }

  @Selector()
  static tiresReportFilter(state: FiltersStateModel) {
    return state.tiresReportFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresByLevelSummaryFilter(state: FiltersStateModel) {
    return state.tiresByLevelSummaryFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresByStockableSummaryFilter(state: FiltersStateModel) {
    return state.tiresByStockableSummaryFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresByRotationSummaryFilter(state: FiltersStateModel) {
    return state.tiresByRotationSummaryFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresByPaymentSummaryFilter(state: FiltersStateModel) {
    return state.tiresByPaymentSummaryFilter ?? defaultPeriodFilter(PeriodType.Yesterday)
  }

  @Selector()
  static tiresListFilters(state: FiltersStateModel) {
    return state.tiresListFilters ?? { page: 1 }
  }

  @Selector()
  static tiresListColumnsVisibility(state: FiltersStateModel) {
    return state.tiresListColumnsVisibility
  }

  @Selector()
  static tiresListAdvancedMode(state: FiltersStateModel) {
    return state.tiresListAdvancedMode
  }

  @Selector()
  static tiresListReturnUrl(state: FiltersStateModel) {
    return state.tiresListReturnUrl
  }

  @Selector()
  static tireInventorySummaryFilter(state: FiltersStateModel) {
    return state.tireInventorySummaryFilter ?? { locationIds: [] }
  }

  @Selector()
  static tireInventoryListFilters(state: FiltersStateModel) {
    return state.tireInventoryListFilters ?? { page: 1 }
  }

  @Selector()
  static usafOverviewFilters(state: FiltersStateModel) {
    return state.usafOverviewFilters ?? { page: 1 }
  }

  @Selector()
  static usafOverviewGetData(state: FiltersStateModel) {
    return state.usafOverviewGetData
  }

  @Selector()
  static usafOrdersListFilters(state: FiltersStateModel) {
    return state.usafOrdersListFilters ?? { page: 1 }
  }

  @Selector()
  static usafOrderLinesListFilters(state: FiltersStateModel) {
    return state.usafOrderLinesListFilters ?? { page: 1 }
  }

  @Selector()
  static usafAutoOrdersListFilters(state: FiltersStateModel) {
    return state.usafAutoOrdersListFilters ?? { page: 1 }
  }

  @Selector()
  static usafLeftoversListFilters(state: FiltersStateModel) {
    return state.usafLeftoversListFilters ?? { page: 1 }
  }

  @Selector()
  static usafOverdueOrdersListFilters(state: FiltersStateModel) {
    return state.usafOverdueOrdersListFilters ?? { page: 1 }
  }

  @Selector()
  static usafInvoicesListFilters(state: FiltersStateModel) {
    return state.usafInvoicesListFilters ?? { page: 1 }
  }

  @Selector()
  static usafListsAdvancedMode(state: FiltersStateModel) {
    return state.usafListsAdvancedMode
  }

  @Selector()
  static usafRefillSelectedLocationIds(state: FiltersStateModel) {
    return state.usafRefillSelectedLocationIds ?? []
  }

  @Selector()
  static tireInventoryListColumnsVisibility(state: FiltersStateModel) {
    return state.tireInventoryListColumnsVisibility
  }

  @Selector()
  static tireInventoryListAdvancedMode(state: FiltersStateModel) {
    return state.tireInventoryListAdvancedMode
  }

  @Selector()
  static tireInventoryListReturnUrl(state: FiltersStateModel) {
    return state.tireInventoryListReturnUrl
  }

  @Selector()
  static lostTiresListFilters(state: FiltersStateModel) {
    return state.lostTiresListFilters ?? { page: 1 }
  }

  @Selector()
  static lostTiresListColumnsVisibility(state: FiltersStateModel) {
    return state.lostTiresListColumnsVisibility
  }

  @Selector()
  static lostTiresListAdvancedMode(state: FiltersStateModel) {
    return state.lostTiresListAdvancedMode
  }

  @Selector()
  static adminUsersListFilters(state: FiltersStateModel) {
    return state.adminUsersListFilters ?? { page: 1 }
  }

  @Selector()
  static adminUsersListColumnsVisibility(state: FiltersStateModel) {
    return state.adminUsersListColumnsVisibility
  }

  @Selector()
  static adminUsersListAdvancedMode(state: FiltersStateModel) {
    return state.adminUsersListAdvancedMode
  }

  @Selector()
  static adminLocationsListFilters(state: FiltersStateModel) {
    return (
      state.adminLocationsListFilters ?? {
        page: 1,
        sort: { active: 'takeOverDate', direction: 'desc' }
      }
    )
  }

  @Selector()
  static adminLocationsListColumnsVisibility(state: FiltersStateModel) {
    return state.adminLocationsListColumnsVisibility
  }

  @Selector()
  static adminLocationsListAdvancedMode(state: FiltersStateModel) {
    return state.adminLocationsListAdvancedMode
  }

  @Selector()
  static yearlyTargetsFilters(state: FiltersStateModel) {
    return state.yearlyTargetsFilters ?? { page: 1, sort: { active: 'name', direction: 'asc' } }
  }

  @Selector()
  static yearlyTargetsColumnsVisibility(state: FiltersStateModel) {
    return state.yearlyTargetsColumnsVisibility
  }

  @Selector()
  static yearlyTargetsAdvancedMode(state: FiltersStateModel) {
    return state.yearlyTargetsAdvancedMode
  }

  @Selector()
  static adminYearlyTargetsFilters(state: FiltersStateModel) {
    return state.adminYearlyTargetsFilters ?? { page: 1, sort: { active: 'name', direction: 'asc' } }
  }

  @Selector()
  static adminYearlyTargetsColumnsVisibility(state: FiltersStateModel) {
    return state.adminYearlyTargetsColumnsVisibility
  }

  @Selector()
  static adminYearlyTargetsAdvancedMode(state: FiltersStateModel) {
    return state.adminYearlyTargetsAdvancedMode
  }

  @Selector()
  static adminMonthlyTargetsFilters(state: FiltersStateModel) {
    return (
      state.adminMonthlyTargetsFilters ?? { page: 1, sort: { active: 'locationName', direction: 'asc' } }
    )
  }

  @Selector()
  static adminMonthlyTargetsColumnsVisibility(state: FiltersStateModel) {
    return state.adminMonthlyTargetsColumnsVisibility
  }

  @Selector()
  static adminMonthlyTargetsAdvancedMode(state: FiltersStateModel) {
    return state.adminMonthlyTargetsAdvancedMode
  }

  @Selector()
  static adminPromotionsListSortState(state: FiltersStateModel) {
    return state.adminPromotionsListSortState
  }

  @Selector()
  static salariesSummarySortState(state: FiltersStateModel) {
    return state.salariesSummarySortState
  }

  @Selector()
  static trackerLocationIds(state: FiltersStateModel) {
    return state.trackerLocationIds ?? []
  }

  @Selector()
  static locationId(state: FiltersStateModel) {
    return state.locationId
  }

  @Selector()
  static communicationTypeFilter(state: FiltersStateModel) {
    return (
      state.communicationTypeFilter ?? {
        locationIds: []
      }
    )
  }

  @Selector()
  static targetsHistoryFilter(state: FiltersStateModel) {
    return (
      state.targetsHistoryFilter ?? {
        locationIds: [],
        targetType: TargetType.CarBonus
      }
    )
  }

  @Selector()
  static weeklyTargetSelectedLocationIds(state: FiltersStateModel) {
    return state.weeklyTargetSelectedLocationIds
  }

  @Selector()
  static locationsStatusSelectedLocationIds(state: FiltersStateModel) {
    return state.locationsStatusSelectedLocationIds
  }

  @Selector()
  static weeklyTargetRankingSortState(state: FiltersStateModel) {
    return state.weeklyTargetRankingSortState
  }

  @Selector()
  static weeklyTargetRankingSelectedRankingType(state: FiltersStateModel) {
    return state.weeklyTargetRankingSelectedRankingType
  }

  @Selector()
  static periodTargetRankingFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.MonthToDate)?.extraInfo
    return (
      state.periodTargetRankingFilter ?? {
        periodType: PeriodType.MonthToDate,
        ...dateInfo
      }
    )
  }

  @Selector()
  static periodTargetSelectedDivision(state: FiltersStateModel) {
    return state.periodTargetSelectedDivision
  }

  @Selector()
  static periodTargetSelectedLocationIds(state: FiltersStateModel) {
    return state.periodTargetSelectedLocationIds
  }

  @Selector()
  static periodTargetRankingSortState(state: FiltersStateModel) {
    return state.periodTargetRankingSortState
  }

  @Selector()
  static periodTargetRankingSelectedRankingType(state: FiltersStateModel) {
    return state.periodTargetRankingSelectedRankingType
  }

  @Selector()
  static workOrdersSummaryFilter(state: FiltersStateModel) {
    return (
      state.workOrdersSummaryFilter ?? {
        locationIds: []
      }
    )
  }

  @Selector()
  static workOrdersSummarySortState(state: FiltersStateModel) {
    return state.workOrdersSummarySortState
  }

  @Selector()
  static workOrdersListFilters(state: FiltersStateModel) {
    return state.workOrdersListFilters ?? { page: 1 }
  }

  @Selector()
  static workOrdersListColumnsVisibility(state: FiltersStateModel) {
    return state.workOrdersListColumnsVisibility
  }

  @Selector()
  static workOrdersListAdvancedMode(state: FiltersStateModel) {
    return state.workOrdersListAdvancedMode
  }

  @Selector()
  static attendanceExpandedShopsId(state: FiltersStateModel) {
    return state.attendanceExpandedShopsId ?? []
  }

  @Selector()
  static attendanceSelectedWeekNumber(state: FiltersStateModel) {
    return state.attendanceSelectedWeekNumber
  }

  @Selector()
  static attendanceActiveCards(state: FiltersStateModel) {
    return state.attendanceActiveCards ?? []
  }

  @Selector()
  static employeeListFilters(state: FiltersStateModel) {
    return state.employeeListFilters
  }

  @Selector()
  static employeeListColumnsVisibility(state: FiltersStateModel) {
    return state.employeeListColumnsVisibility
  }

  @Selector()
  static employeeListAdvancedMode(state: FiltersStateModel) {
    return state.employeeListAdvancedMode
  }

  @Selector()
  static laborSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.laborSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static laborSummarySortState(state: FiltersStateModel) {
    return state.laborSummarySortState
  }

  @Selector()
  static technicianSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.technicianSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static technicianSummarySortState(state: FiltersStateModel) {
    return state.technicianSummarySortState
  }

  @Selector()
  static technicianListEmployeeId(state: FiltersStateModel) {
    return state.technicianListEmployeeId
  }

  @Selector()
  static technicianListFilters(state: FiltersStateModel) {
    return state.technicianListFilters ?? { page: 1 }
  }

  @Selector()
  static technicianListColumnsVisibility(state: FiltersStateModel) {
    return state.technicianListColumnsVisibility
  }

  @Selector()
  static technicianListAdvancedMode(state: FiltersStateModel) {
    return state.technicianListAdvancedMode
  }

  @Selector()
  static technicianListSelectedDateInfo(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.technicianListSelectedDateInfo ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }
    )
  }

  @Selector()
  static serviceAdvisorSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.serviceAdvisorSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static serviceAdvisorSummarySortState(state: FiltersStateModel) {
    return state.serviceAdvisorSummarySortState
  }

  @Selector()
  static serviceAdvisorListEmployeeId(state: FiltersStateModel) {
    return state.serviceAdvisorListEmployeeId
  }

  @Selector()
  static serviceAdvisorListFilters(state: FiltersStateModel) {
    return state.serviceAdvisorListFilters ?? { page: 1 }
  }

  @Selector()
  static serviceAdvisorListColumnsVisibility(state: FiltersStateModel) {
    return state.serviceAdvisorListColumnsVisibility
  }

  @Selector()
  static serviceAdvisorListAdvancedMode(state: FiltersStateModel) {
    return state.serviceAdvisorListAdvancedMode
  }

  @Selector()
  static serviceAdvisorListSelectedDateInfo(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.serviceAdvisorListSelectedDateInfo ?? {
        periodType: PeriodType.PreviousWeek,
        ...dateInfo
      }
    )
  }

  @Selector()
  static schedulerSelectedShopId(state: FiltersStateModel) {
    return state.schedulerSelectedShopId
  }

  @Selector()
  static schedulerSelectedWeekNumber(state: FiltersStateModel) {
    return state.schedulerSelectedWeekNumber
  }

  @Selector()
  static schedulerShowAllEmployees(state: FiltersStateModel) {
    return state.schedulerShowAllEmployees
  }

  @Selector()
  static compareReportSelectedObjectives(state: FiltersStateModel) {
    return state.compareReportSelectedObjectives
  }

  @Selector()
  static compareReportFilter(state: FiltersStateModel) {
    return state.compareReportFilter
  }

  @Selector()
  static compareReportGetData(state: FiltersStateModel) {
    return state.compareReportGetData
  }

  @Selector()
  static compareReportExpandedShopsId(state: FiltersStateModel) {
    return state.compareReportExpandedShopsId ?? []
  }

  @Selector()
  static compareReportShowCharts(state: FiltersStateModel) {
    return state.compareReportShowCharts ?? true
  }

  @Selector()
  static compareReportDisplayOptions(state: FiltersStateModel) {
    return state.compareReportDisplayOptions
  }

  @Selector()
  static compareObjectivesByGroupSelectedObjectives(state: FiltersStateModel) {
    return state.compareObjectivesByGroupSelectedObjectives
  }

  @Selector()
  static compareObjectivesByGroupNumberOfMonths(state: FiltersStateModel) {
    return state.compareObjectivesByGroupNumberOfMonths
  }

  @Selector()
  static compareObjectivesByGroupLocationIds(state: FiltersStateModel) {
    return state.compareObjectivesByGroupLocationIds ?? []
  }

  @Selector()
  static compareObjectivesByRegionalSelectedObjectives(state: FiltersStateModel) {
    return state.compareObjectivesByRegionalSelectedObjectives
  }

  @Selector()
  static compareObjectivesByRegionalNumberOfMonths(state: FiltersStateModel) {
    return state.compareObjectivesByRegionalNumberOfMonths
  }

  @Selector()
  static compareObjectivesByRegionalLocationIds(state: FiltersStateModel) {
    return state.compareObjectivesByRegionalLocationIds ?? []
  }

  @Selector()
  static objectivesRankingSelectedObjective(state: FiltersStateModel) {
    return state.objectivesRankingSelectedObjective
  }

  @Selector()
  static objectivesRankingSelectedLocationIds(state: FiltersStateModel) {
    return state.objectivesRankingSelectedLocationIds
  }

  @Selector()
  static dailyAverageSelectedMetric(state: FiltersStateModel) {
    return state.dailyAverageSelectedMetric
  }

  @Selector()
  static dailyAverageSelectedLocationIds(state: FiltersStateModel) {
    return state.dailyAverageSelectedLocationIds
  }

  @Selector()
  static targetsDailyAverageSelectedObjective(state: FiltersStateModel) {
    return state.targetsDailyAverageSelectedObjective
  }

  @Selector()
  static targetsDailyAverageSelectedLocationIds(state: FiltersStateModel) {
    return state.targetsDailyAverageSelectedLocationIds
  }

  @Selector()
  static targetsDailyAverageSelectedWeeks(state: FiltersStateModel) {
    return state.targetsDailyAverageSelectedWeeks
  }

  @Selector()
  static dailyAverageSelectedDateInfo(state: FiltersStateModel): IRangeDateInfo {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.dailyAverageSelectedDateInfo ?? {
        periodType: PeriodType.PreviousWeek,
        rangeDate: {
          ...dateInfo
        }
      }
    )
  }

  @Selector()
  static dailyAverageSelectedDateFilter(state: FiltersStateModel) {
    return state.dailyAverageSelectedDateFilter
  }

  @Selector()
  static arMovingAverageFilter(state: FiltersStateModel) {
    return state.arMovingAverageFilter
  }

  @Selector()
  static vendorsFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.MonthToDate)?.extraInfo
    return (
      state.vendorsFilter ?? {
        periodType: PeriodType.MonthToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static vendorsExpandedShopsId(state: FiltersStateModel) {
    return state.vendorsExpandedShopsId ?? []
  }

  @Selector()
  static schedulerListFilter(state: FiltersStateModel) {
    return (
      state.schedulerListFilter ?? {
        locationIds: []
      }
    )
  }

  @Selector()
  static categoryMovingAverageSelectedCategory(state: FiltersStateModel) {
    return state.categoryMovingAverageSelectedCategory
  }

  @Selector()
  static categoryMovingAverageSelectedLocationIds(state: FiltersStateModel) {
    return state.categoryMovingAverageSelectedLocationIds
  }

  @Selector()
  static fluidsSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.fluidsSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static fluidsSummarySortState(state: FiltersStateModel) {
    return state.fluidsSummarySortState
  }

  @Selector()
  static addonsSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.addonsSummaryFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static addonsSummarySortState(state: FiltersStateModel) {
    return state.addonsSummarySortState
  }

  @Selector()
  static fluidsListFilters(state: FiltersStateModel) {
    return state.fluidsListFilters ?? { page: 1 }
  }

  @Selector()
  static fluidsListColumnsVisibility(state: FiltersStateModel) {
    return state.fluidsListColumnsVisibility
  }

  @Selector()
  static fluidsListAdvancedMode(state: FiltersStateModel) {
    return state.fluidsListAdvancedMode
  }

  @Selector()
  static cashDrawerFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    return (
      state.cashDrawerFilter ?? {
        periodType: PeriodType.WeekToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static cashDrawerSortState(state: FiltersStateModel) {
    return state.cashDrawerSortState
  }

  @Selector()
  static weeklyTireCountFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    return (
      state.weeklyTireCountFilter ?? {
        periodType: PeriodType.WeekToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static weeklyTireCountSortState(state: FiltersStateModel) {
    return state.weeklyTireCountSortState
  }

  @Selector()
  static weeklyTireReportFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    return (
      state.weeklyTireReportFilter ?? {
        periodType: PeriodType.WeekToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static weeklyTireReportSortState(state: FiltersStateModel) {
    return state.weeklyTireReportSortState
  }

  @Selector()
  static curbAppealFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    return (
      state.curbAppealFilter ?? {
        periodType: PeriodType.WeekToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static curbAppealSortState(state: FiltersStateModel) {
    return state.curbAppealSortState
  }

  @Selector()
  static showroomFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    return (
      state.showroomFilter ?? {
        periodType: PeriodType.WeekToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static showroomSortState(state: FiltersStateModel) {
    return state.showroomSortState
  }

  @Selector()
  static employeeEfficiencyRankingSelectedLocationIds(state: FiltersStateModel) {
    return state.employeeEfficiencyRankingSelectedLocationIds
  }

  @Selector()
  static employeeEfficiencyRankingSelectedType(state: FiltersStateModel) {
    return state.employeeEfficiencyRankingSelectedType
  }

  @Selector()
  static scheduleAttendanceFilter(state: FiltersStateModel) {
    return (
      state.scheduleAttendanceFilter ?? {
        locationIds: []
      }
    )
  }

  @Selector()
  static scheduleAttendanceExpandedShopsId(state: FiltersStateModel) {
    return state.scheduleAttendanceExpandedShopsId ?? []
  }

  @Selector()
  static upsellFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.PreviousWeek
    )?.extraInfo
    return (
      state.upsellFilter ?? {
        periodType: PeriodType.PreviousWeek,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static upsellListFilters(state: FiltersStateModel) {
    return state.upsellListFilters ?? { page: 1 }
  }

  @Selector()
  static upsellListColumnsVisibility(state: FiltersStateModel) {
    return state.upsellListColumnsVisibility
  }

  @Selector()
  static upsellListAdvancedMode(state: FiltersStateModel) {
    return state.upsellListAdvancedMode
  }

  @Selector()
  static invoicePackageLinesFilters(state: FiltersStateModel) {
    return state.invoicePackageLinesFilters ?? { page: 1 }
  }

  @Selector()
  static invoicePackageLinesColumnsVisibility(state: FiltersStateModel) {
    return state.invoicePackageLinesColumnsVisibility
  }

  @Selector()
  static invoicePackageLinesAdvancedMode(state: FiltersStateModel) {
    return state.invoicePackageLinesAdvancedMode
  }

  @Selector()
  static invoicePackageLinesReturnUrl(state: FiltersStateModel) {
    return state.invoicePackageLinesReturnUrl
  }

  @Selector()
  static salariesSummaryFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find(
      (el) => el.value === PeriodType.SixMonthToDate
    )?.extraInfo
    return (
      state.salariesSummaryFilter ?? {
        periodType: PeriodType.SixMonthToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static salariesDetailFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.MonthToDate)?.extraInfo
    return (
      state.salariesDetailFilter ?? {
        periodType: PeriodType.MonthToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static salariesDetailReturnUrl(state: FiltersStateModel) {
    return state.salariesDetailReturnUrl
  }

  @Selector()
  static adjustedGPFilter(state: FiltersStateModel) {
    return (
      state.adjustedGPFilter ?? {
        locationIds: []
      }
    )
  }

  @Selector()
  static googleReviewsFilter(state: FiltersStateModel) {
    return state.googleReviewsFilter ?? {}
  }

  @Selector()
  static adminWeeklyTargetsSelectedWeek(state: FiltersStateModel) {
    return state.adminWeeklyTargetsSelectedWeek
  }

  @Selector()
  static adminWeeklyTargetsLocationIds(state: FiltersStateModel) {
    return state.adminWeeklyTargetsLocationIds ?? []
  }

  @Selector()
  static adminViewTargetsSelectedTab(state: FiltersStateModel) {
    return state.adminViewTargetsSelectedTab
  }

  @Selector()
  static adminMonthlyViewTargetsSelectedMonth(state: FiltersStateModel) {
    return state.adminMonthlyViewTargetsSelectedMonth
  }

  @Selector()
  static adminMonthlyViewTargetsLocationIds(state: FiltersStateModel) {
    return state.adminMonthlyViewTargetsLocationIds ?? []
  }

  @Selector()
  static contactsReportConfig(state: FiltersStateModel) {
    return state.contactsReportConfig
  }

  @Selector()
  static callIntelligenceDashboardFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.YearToDate)?.extraInfo
    return (
      state.callIntelligenceDashboardFilter ?? {
        periodType: PeriodType.YearToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static callIntelligenceDrilldownFilter(state: FiltersStateModel) {
    const dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.YearToDate)?.extraInfo
    return (
      state.callIntelligenceDrilldownFilter ?? {
        periodType: PeriodType.YearToDate,
        locationIds: [],
        ...dateInfo
      }
    )
  }

  @Selector()
  static callIntelligenceDrilldownSortState(state: FiltersStateModel) {
    return state.callIntelligenceDrilldownSortState
  }

  @Selector()
  static wowSelectedLocationIds(state: FiltersStateModel) {
    return state.wowSelectedLocationIds
  }

  @Selector()
  static wowSelectedWeek(state: FiltersStateModel) {
    return state.wowSelectedWeek
  }

  @Selector()
  static wowSelectedCompareWeek(state: FiltersStateModel) {
    return state.wowSelectedCompareWeek
  }

  ngxsOnInit(ctx: StateContext<FiltersStateModel>) {
    this.restoreFromLocalStorage(ctx)
  }

  @Action(RestoreFiltersFromLocalStorage)
  restoreFiltersFromLocalStorage(ctx: StateContext<FiltersStateModel>) {
    this.restoreFromLocalStorage(ctx)
  }

  private restoreFromLocalStorage(ctx: StateContext<FiltersStateModel>) {
    const persistObj = localStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS)
    if (persistObj) {
      const filters: Partial<FiltersStateModel> = JSON.parse(persistObj)
      if (!isEmpty(filters) && isObject(filters)) {
        for (const key in filters) {
          ctx.patchState({ [key]: filters[key as FilterStateKeys] })
        }
      }
    }
  }

  @Action(SetFilters)
  setFilters(ctx: StateContext<FiltersStateModel>, { filters }: SetFilters) {
    if (isEmpty(filters)) {
      return
    }
    for (const key in filters) {
      ctx.patchState({ [key]: filters[key as FilterStateKeys] })
    }
    this.saveFiltersToLocalStorage(ctx.getState())
    this.updateQueryParams(ctx.getState())
  }

  @Action(SetAdvancedTableFilters)
  setAdvancedTableFilters(
    ctx: StateContext<FiltersStateModel>,
    { key, value }: SetAdvancedTableFilters
  ) {
    if (!value?.filters) {
      return
    }
    const filters: ITableFilter | undefined = { ...value.filters }
    if (isEmpty(filters)) {
      return
    }
    for (const key in filters) {
      filters[key] = omit(filters[key], ['enumItems'])
    }

    ctx.patchState({ [key]: { ...value, filters } })
    this.saveFiltersToLocalStorage(ctx.getState())
    this.updateQueryParams(ctx.getState())
  }

  @Action(SetFiltersFromUrlQueryParams)
  setFiltersFromUrlQueryParams(
    ctx: StateContext<FiltersStateModel>,
    { filtersToken }: SetFiltersFromUrlQueryParams
  ) {
    try {
      if (!filtersToken || filtersToken === '') {
        return
      }
      const filters = jwtDecode<FiltersStateModel>(filtersToken)
      if (!filters || isEmpty(filters)) {
        return
      }
      if (filters.appVersion !== AppVersion) {
        this.router.navigateByUrl(`/dashboard/${NewVersionIndicator}`)
        return
      }

      const validFilters = this.pickValidFilters(filters)
      ctx.patchState({ ...validFilters })
      this.saveFiltersToLocalStorage(ctx.getState())
      this.updateQueryParams(ctx.getState())
    } catch (_) {
      return
    }
  }

  @Action(SetUrlQueryParamsFromState)
  setUrlQueryParamsFromState(ctx: StateContext<FiltersStateModel>) {
    this.updateQueryParams(ctx.getState())
  }

  private updateQueryParams(state: FiltersStateModel) {
    const currentUrl = new URL(document.URL)
    let currentFilters = {}
    const filtersToken = Object.fromEntries(new URLSearchParams(currentUrl.search) as any)['filters']
    if (filtersToken && filtersToken !== '') {
      try {
        currentFilters = jwtDecode<FiltersStateModel>(filtersToken)
      } catch (_) {
        currentFilters = {}
      }
    }
    const currentPath = this.router.url.split('?')[0]
    const validFilters = this.pickValidFilters({ ...currentFilters, ...state })

    if (!validFilters) {
      this.location.replaceState(currentPath)
      return
    }

    this.location.replaceState(
      `${currentPath}?filters=${sign(
        { ...validFilters, appVersion: AppVersion },
        environment.jwtSecret
      )}`
    )
  }

  private pickValidFilters(filters: Partial<FiltersStateModel>) {
    const currentPath = this.router.url.split('?')[0]
    const pageQueryParams = PageQueryParamsMap[currentPath]
    if (!pageQueryParams) {
      return
    }
    return pickBy(filters, (value, key) => pageQueryParams.find((el) => el === key))
  }

  private static evaluateBoolean(value?: string | boolean) {
    return value !== undefined ? value === 'true' || value === true : true
  }

  private saveFiltersToLocalStorage(state: FiltersStateModel) {
    const persistObj = pick(state, PersistFilters)
    if (persistObj && !isEmpty(persistObj)) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.FILTERS, JSON.stringify(persistObj))
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.FILTERS)
    }
  }
}
